
import { defineComponent, PropType } from 'vue'

import {
  CompiledPinnedReportT,
  ILoadingValue,
  IReportMetadata,
  LoadingConditions,
  ReportMeta,
  ReportPageT
} from '~/types/reportTypes'
import { ReportLevel, RepositoryCoverageReportItem } from '~/types/types'

/**
 * Component with the markup for code coverage report
 */
export default defineComponent({
  name: 'PinnedCodeCoverageReport',
  props: {
    allowPinningReports: {
      type: Boolean,
      required: true
    },
    coverageList: {
      type: Array as PropType<Array<RepositoryCoverageReportItem>>,
      default: () => []
    },
    error: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: true
    },
    loadingValue: {
      type: Object as PropType<ILoadingValue>,
      required: true
    },
    metadata: {
      type: [Object, null] as PropType<IReportMetadata | null>
    },
    owner: {
      type: String,
      required: true
    },
    pinnedReports: {
      type: Array as PropType<Array<CompiledPinnedReportT>>,
      required: true
    },
    provider: {
      type: String,
      required: true
    },
    reportKey: {
      type: String as PropType<ReportPageT>,
      required: true
    },
    reportSlot: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      revealReportControls: false,
      ReportLevel,
      timeoutId: null as ReturnType<typeof setTimeout> | null
    }
  },
  computed: {
    currentSelection(): string {
      const currentSelection = this.metadata?.filter || this.reportKey

      if (this.loadingValue.status || !currentSelection) {
        return ''
      }
      return this.metadata?.filter || this.reportKey
    },
    helpText(): string {
      const { helpText } = ReportMeta[this.reportKey]
      return helpText ?? ''
    },
    isReportControlsChange(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_CONTROLS_CHANGE
    },
    isReportGettingSwapped(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_SWAP
    },
    isReportWidgetDataFetch(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_WIDGET_DATA_FETCH
    },
    loaderCount(): number {
      const defaultLoaderCount = 7
      let loaderCountFromStore = null

      if (process.client) {
        loaderCountFromStore = this.$localStore.get(
          `${this.provider}-${this.owner}`,
          'code-coverage-loader-count'
        ) as number
      }

      // Ensure the loader count does not exceed `7` since the report widget shows only max `7` entries
      if (loaderCountFromStore) {
        return loaderCountFromStore > defaultLoaderCount ? defaultLoaderCount : loaderCountFromStore
      }
      return defaultLoaderCount
    },
    selectedSortType(): string {
      if (this.loadingValue.status) {
        return ''
      }

      return (
        this.$cookies.get(
          `${this.provider}_${this.owner}_pinned-code-coverage-sort-type_${this.reportSlot}`
        ) || ''
      )
    }
  },
  methods: {
    /**
     * Method to hide report controls after a `1s` delay
     * Report controls remain visible if the user comes back before `1s`
     *
     * @returns {void}
     */
    hideReportControls(): void {
      this.timeoutId = setTimeout(() => {
        this.revealReportControls = false
      }, 1000)
    },

    /**
     * Navigate to the code coverage report page
     *
     * @returns {void}
     */
    navigateToReportPage(): void {
      // Code coverage report is only possible for team level
      const reportPageUrl = this.$generateRoute(['reports', this.reportKey], false, true)
      this.$router.push(reportPageUrl)
    },

    /**
     * Method to reveal report controls after clearing the currently active timer
     *
     * @returns {void}
     */
    showReportControls(): void {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.revealReportControls = true
    },

    /**
     * Method to emit an event by the name `update-report-controls`
     *
     * @param {string}} newSortType
     * @returns {void}
     */
    updateSortTypeHandler(newSortType: string): void {
      // Emit the `update-report-controls` event signifying a change in sort type
      this.$emit('update-report-controls', this.reportSlot, newSortType)
    }
  }
})
